import React, { useRef, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Disclosure } from '@headlessui/react';
import { Bars3Icon as MenuIcon, XMarkIcon as XIcon } from '@heroicons/react/24/outline';

interface MenuItem {
  title: string;
  path?: string;
  submenu?: MenuItem[];
}

const Navigation: React.FC = () => {
  const menuItems: MenuItem[] = [
    { title: 'Главная', path: '/' },
    { title: 'Каталог', path: '/catalog' },
    { title: 'Прайслисты', path: '/price-lists' },
    { title: 'Техподдержка', path: '/support' },
    {
      title: 'Сотрудничество',
      submenu: [
        { title: 'Для всех', path: '/partnership/all' },
        { title: 'Для торговых компаний', path: '/partnership/wholesale' },
        { title: 'Для проектировщиков', path: '/partnership/designers' }
      ]
    },
    {
      title: 'О компании',
      submenu: [
        { title: 'О нас', path: '/about/company' },
        { title: 'Отзывы клиентов', path: '/about/reviews' },
        { title: 'Дилерские сертификаты', path: '/about/certificates' },
        { title: 'Наши партнеры', path: '/about/partners' }
      ]
    },
    { title: 'Контакты', path: '/contacts' }
  ];

  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimeoutRef.current = setTimeout(() => {
      setOpenIndex(null);
    }, 180);
  };

  const handleOpen = (index: number) => {
    cancelClose();
    setOpenIndex(index);
  };

  return (
    <Disclosure as="nav" className="sticky top-0 z-[70] border-b border-white/70 bg-white/85 backdrop-blur-xl shadow-[0_16px_30px_-26px_rgba(30,64,175,0.75)]">
      {({ open }) => (
        <>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center">
                <div className="hidden md:block">
                  <div className="flex items-baseline space-x-1">
                    {menuItems.map((item, index) => {
                      const isOpen = openIndex === index;

                      if (item.path) {
                        return (
                          <NavLink
                            key={index}
                            to={item.path}
                            onMouseEnter={scheduleClose}
                            className={({ isActive }) =>
                              `px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                                isActive
                                  ? 'text-primary bg-primary/10 shadow-sm'
                                  : 'text-slate-700 hover:text-primary hover:bg-primary/10'
                              }`
                            }
                          >
                            {item.title}
                          </NavLink>
                        );
                      }

                      return (
                        <div
                          key={index}
                          className="relative"
                          onMouseEnter={() => handleOpen(index)}
                          onMouseLeave={scheduleClose}
                        >
                          <button
                            type="button"
                            onClick={() => setOpenIndex(isOpen ? null : index)}
                            className={`flex items-center px-4 py-2 rounded-xl text-sm font-semibold cursor-pointer transition-all ${
                              isOpen
                                ? 'text-primary bg-primary/10'
                                : 'text-slate-700 hover:text-primary hover:bg-primary/5'
                            }`}
                          >
                            {item.title}
                            <svg
                              className={`w-4 h-4 ml-1 transform transition-transform ${
                                isOpen ? 'rotate-180' : ''
                              }`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          {item.submenu && isOpen && (
                            <div
                              className="absolute left-0 top-full w-64 pt-2 z-[80]"
                              onMouseEnter={cancelClose}
                              onMouseLeave={scheduleClose}
                            >
                              <div className="rounded-2xl shadow-xl bg-white border border-blue-50 overflow-hidden">
                                {item.submenu.map((subItem, subIndex) => (
                                  <Link
                                    key={subIndex}
                                    to={subItem.path || '#'}
                                    onClick={() => setOpenIndex(null)}
                                    className="block px-4 py-3 text-sm text-slate-700 hover:bg-gradient-to-r hover:from-primary/10 hover:to-transparent hover:text-primary transition-colors"
                                  >
                                    {subItem.title}
                                  </Link>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="md:hidden">
                <Disclosure.Button className="inline-flex items-center justify-center p-2 rounded-lg text-slate-700 hover:text-primary hover:bg-primary/10 transition-colors">
                  {open ? (
                    <XIcon className="block h-6 w-6" />
                  ) : (
                    <MenuIcon className="block h-6 w-6" />
                  )}
                </Disclosure.Button>
              </div>
            </div>
          </div>

          <Disclosure.Panel className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-white/95 backdrop-blur">
              {menuItems.map((item, index) => (
                <div key={index}>
                  {item.path ? (
                    <NavLink
                      to={item.path}
                      className={({ isActive }) =>
                        `block px-3 py-2 rounded-lg text-base font-semibold ${
                          isActive
                            ? 'text-primary bg-primary/10'
                            : 'text-slate-700 hover:text-primary hover:bg-primary/10'
                        }`
                      }
                    >
                      {item.title}
                    </NavLink>
                  ) : (
                    <>
                      <div className="block px-3 py-2 rounded-lg text-base font-semibold text-slate-700">
                        {item.title}
                      </div>
                      {item.submenu && (
                        <div className="pl-4 bg-slate-50 rounded-lg">
                          {item.submenu.map((subItem, subIndex) => (
                            <Link
                              key={subIndex}
                              to={subItem.path || '#'}
                              className="block px-3 py-2 rounded-md text-base font-medium text-slate-600 hover:text-primary hover:bg-primary/10"
                            >
                              {subItem.title}
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
};

export default Navigation;
